import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from '../utils/toast';

const WYSIWYGEditor = ({ value, onChange, placeholder = 'Skriv her...', minHeight = '150px' }) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const [isBold, setIsBold] = useState(false);
  const [showHtmlMode, setShowHtmlMode] = useState(false);
  const [htmlCode, setHtmlCode] = useState('');

  useEffect(() => {
    if (editorRef.current && !editorRef.current.contains(document.activeElement) && !showHtmlMode) {
      const newValue = value || '';
      // Only update innerHTML when editor is not focused and not in HTML mode to avoid cursor jump
      if (editorRef.current.innerHTML !== newValue) {
        editorRef.current.innerHTML = newValue;
      }
    }
  }, [value, showHtmlMode]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateToolbarState();
  };

  const toggleBold = () => {
    execCommand('bold');
  };

  const toggleItalic = () => {
    execCommand('italic');
  };

  const toggleUppercase = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const span = document.createElement('span');
    span.style.textTransform = 'uppercase';
    
    try {
      range.surroundContents(span);
    } catch (e) {
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
    handleInput();
  };

  const setFontSize = (size) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const span = document.createElement('span');
    span.style.fontSize = size + 'px';
    
    try {
      range.surroundContents(span);
    } catch (e) {
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
    handleInput();
  };

  const setTextColor = (colorClass) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const span = document.createElement('span');
    span.className = colorClass;
    
    try {
      range.surroundContents(span);
    } catch (e) {
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
    handleInput();
  };

  const insertLink = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount || selection.isCollapsed) {
      toast.warning('Vennligst marker teksten du vil gjøre om til en lenke');
      return;
    }

    const url = prompt('Lim inn URL (https://...)');
    if (!url) return;

    try {
      const urlObj = new URL(url);
      if (!/^https?:$/.test(urlObj.protocol)) {
        throw new Error('Kun http og https er tillatt');
      }
    } catch {
      toast.error('Ugyldig URL. Må starte med http:// eller https://');
      return;
    }

    const openInNewTab = confirm('Åpne lenke i ny fane?');
    const range = selection.getRangeAt(0);
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    if (openInNewTab) {
      a.target = '_blank';
    }
    
    try {
      range.surroundContents(a);
    } catch (e) {
      const contents = range.extractContents();
      a.appendChild(contents);
      range.insertNode(a);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
    handleInput();
  };

  const updateToolbarState = () => {
    setIsBold(document.queryCommandState('bold'));
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      toggleBold();
    }
  };

  const toggleHtmlMode = () => {
    if (!showHtmlMode) {
      // Switching TO HTML mode - get current HTML
      setHtmlCode(editorRef.current?.innerHTML || '');
    } else {
      // Switching FROM HTML mode - call onChange with the HTML code
      onChange(htmlCode);
    }
    setShowHtmlMode(!showHtmlMode);
  };

  const handleHtmlCodeChange = (e) => {
    setHtmlCode(e.target.value);
  };

  // Update editor when switching back from HTML mode
  useEffect(() => {
    if (!showHtmlMode && editorRef.current && htmlCode && editorRef.current.innerHTML !== htmlCode) {
      // Preserve the HTML exactly as it is
      editorRef.current.innerHTML = htmlCode;
      // Trigger onChange to ensure parent component has the correct value
      onChange(htmlCode);
    }
  }, [showHtmlMode]);

  return (
    <div className="wysiwyg-editor">
      <div className="wysiwyg-toolbar">
        <button
          type="button"
          className={`toolbar-btn ${isBold ? 'active' : ''}`}
          onClick={toggleBold}
          title="Fet (Ctrl+B)"
          aria-pressed={isBold}
        >
          <strong>B</strong>
        </button>

        <button
          type="button"
          className="toolbar-btn"
          onClick={toggleItalic}
          title="Kursiv"
        >
          <em>I</em>
        </button>

        <button
          type="button"
          className="toolbar-btn"
          onClick={toggleUppercase}
          title="Versaler (store bokstaver)"
        >
          <span style={{ fontSize: '0.75rem' }}>ABC</span>
        </button>

        <select
          className="toolbar-select"
          onChange={(e) => e.target.value && setFontSize(e.target.value)}
          defaultValue=""
          style={{ color: 'white' }}
        >
          <option value="" disabled style={{ color: 'white', background: '#1a1a1a' }}>{t('common.size')}</option>
          <option value="12" style={{ color: 'white', background: '#1a1a1a' }}>12px</option>
          <option value="14" style={{ color: 'white', background: '#1a1a1a' }}>14px</option>
          <option value="16" style={{ color: 'white', background: '#1a1a1a' }}>16px</option>
          <option value="18" style={{ color: 'white', background: '#1a1a1a' }}>18px</option>
          <option value="20" style={{ color: 'white', background: '#1a1a1a' }}>20px</option>
          <option value="24" style={{ color: 'white', background: '#1a1a1a' }}>24px</option>
          <option value="28" style={{ color: 'white', background: '#1a1a1a' }}>28px</option>
          <option value="32" style={{ color: 'white', background: '#1a1a1a' }}>32px</option>
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.75rem', opacity: 0.7 }}>{t('common.color')}:</label>
          <input
            type="color"
            className="toolbar-color-picker"
            onChange={(e) => {
              const selection = window.getSelection();
              if (selection.rangeCount) {
                const range = selection.getRangeAt(0);
                const span = document.createElement('span');
                span.style.color = e.target.value;
                try {
                  range.surroundContents(span);
                } catch (err) {
                  const contents = range.extractContents();
                  span.appendChild(contents);
                  range.insertNode(span);
                }
                handleInput();
              }
            }}
            title="Velg tekstfarge"
          />
        </div>

        <button
          type="button"
          className="toolbar-btn"
          onClick={insertLink}
          title="Sett inn lenke"
        >
          🔗
        </button>

        <button
          type="button"
          className={`toolbar-btn ${showHtmlMode ? 'active' : ''}`}
          onClick={toggleHtmlMode}
          title="HTML-kode visning"
          style={{ marginLeft: '0.5rem' }}
        >
          &lt;/&gt;
        </button>
      </div>

      {showHtmlMode ? (
        <textarea
          value={htmlCode}
          onChange={handleHtmlCodeChange}
          placeholder="Skriv eller rediger HTML-koden her..."
          style={{
            minHeight,
            width: '100%',
            padding: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            background: 'rgba(0, 0, 0, 0.3)',
            color: '#50E3C2',
            outline: 'none',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            resize: 'vertical',
            maxHeight: '400px'
          }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          className="wysiwyg-content"
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onMouseUp={updateToolbarState}
          onKeyUp={updateToolbarState}
          data-placeholder={placeholder}
          style={{ minHeight }}
        />
      )}

      <style>{`
        .wysiwyg-editor {
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          overflow: hidden;
        }

        .wysiwyg-toolbar {
          display: flex;
          gap: 0.5rem;
          padding: 0.5rem;
          background: rgba(0, 0, 0, 0.2);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          align-items: center;
        }

        .toolbar-btn {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: white;
          padding: 0.35rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s;
        }

        .toolbar-btn:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        .toolbar-btn.active {
          background: #50E3C2;
          color: #0F0F0F;
          border-color: #50E3C2;
        }

        .toolbar-select {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: white !important;
          padding: 0.35rem 0.5rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
          max-width: 120px;
          min-width: 100px;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
        }

        .toolbar-select option {
          background: #1a1a1a !important;
          background-color: #1a1a1a !important;
          color: white !important;
          color: #ffffff !important;
          -webkit-text-fill-color: white !important;
        }
        
        .toolbar-select:focus option,
        .toolbar-select option:hover,
        .toolbar-select option:checked {
          background: #2a2a2a !important;
          background-color: #2a2a2a !important;
          color: white !important;
          color: #ffffff !important;
        }

        .toolbar-color-picker {
          width: 40px;
          height: 32px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          cursor: pointer;
          background: rgba(255, 255, 255, 0.1);
          padding: 2px;
        }

        .toolbar-color-picker::-webkit-color-swatch-wrapper {
          padding: 0;
        }

        .toolbar-color-picker::-webkit-color-swatch {
          border: none;
          border-radius: 2px;
        }

        .toolbar-separator {
          width: 1px;
          background: rgba(255, 255, 255, 0.1);
          margin: 0 0.25rem;
        }

        .wysiwyg-content {
          padding: 1rem;
          min-height: 150px;
          color: white;
          outline: none;
          line-height: 1.6;
        }

        .wysiwyg-content:empty:before {
          content: attr(data-placeholder);
          color: #666;
        }

        .wysiwyg-content p {
          margin: 0 0 0.5rem 0;
        }

        .wysiwyg-content strong {
          font-weight: 600;
        }

        .wysiwyg-content a {
          color: #50E3C2;
          text-decoration: underline;
        }

        .wysiwyg-content .text-sm {
          font-size: 0.875rem;
        }

        .wysiwyg-content .text-base {
          font-size: 1rem;
        }

        .wysiwyg-content .text-lg {
          font-size: 1.25rem;
        }

        .wysiwyg-content .text-brand {
          color: #50E3C2;
        }

        .wysiwyg-content .text-white {
          color: white;
        }

        .wysiwyg-content .text-gray {
          color: #999;
        }

        .wysiwyg-content .text-red {
          color: #FF6B6B;
        }
      `}</style>
    </div>
  );
};

export default WYSIWYGEditor;
